import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCart } from "@/context/CartContext";

// Fallback slides until the API delivers the admin-configured banners.
const FALLBACK_SLIDES = [
  "https://images.unsplash.com/photo-1620783770629-122b7f187703?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwzfHxlY29tbWVyY2UlMjBiYW5uZXIlMjBlbGVjdHJvbmljc3xlbnwwfHx8fDE3ODkyMzY1NDJ8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxtb2JpbGUlMjBwaG9uZSUyMGFjY2Vzc29yaWVzfGVufDB8fHx8MTc4OTIzNjU0Mnww&ixlib=rb-4.1.0&q=85",
];

// Noon-style full-width image-only banner strip. Slides come from the admin
// (up to 3, stored via PUT /api/admin/hero) and rotate every 5 seconds.
export default function Hero() {
  const { heroSlides } = useCart();
  const slides = heroSlides?.length ? heroSlides.slice(0, 3) : FALLBACK_SLIDES;
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setSlide((s) => (s + 1) % slides.length), 5000);
    return () => clearInterval(id);
  }, [slides.length]);

  const go = (dir) => setSlide((s) => (s + dir + slides.length) % slides.length);

  return (
    <section id="top" data-testid="hero-section" className="pt-[132px] md:pt-[124px]">
      <div className="relative h-[220px] w-full overflow-hidden bg-white sm:h-[300px] md:h-[360px] lg:h-[400px]">
        <AnimatePresence mode="sync">
          <motion.img
            key={slide}
            src={slides[slide % slides.length]}
            alt={`Cambroo banner ${slide + 1}`}
            data-testid="hero-image"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0 h-full w-full object-cover"
          />
        </AnimatePresence>

        {slides.length > 1 && (
          <>
            <button
              data-testid="hero-prev"
              onClick={() => go(-1)}
              className="absolute start-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-brandink shadow transition-colors hover:bg-white"
              aria-label="Previous banner"
            >
              <ChevronLeft className="h-5 w-5 rtl:-scale-x-100" />
            </button>
            <button
              data-testid="hero-next"
              onClick={() => go(1)}
              className="absolute end-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-brandink shadow transition-colors hover:bg-white"
              aria-label="Next banner"
            >
              <ChevronRight className="h-5 w-5 rtl:-scale-x-100" />
            </button>
            <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-2 rtl:translate-x-1/2">
              {slides.map((_, i) => (
                <button
                  key={i}
                  data-testid={`hero-dot-${i}`}
                  onClick={() => setSlide(i)}
                  aria-label={`Banner ${i + 1}`}
                  className={`h-2 rounded-full transition-all ${i === slide ? "w-6 bg-brandink" : "w-2 bg-brandink/30"}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
